import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { get, post, put, del } from '@/services/ApiService';
import {
    GET_SHOP_ABOUT_US,
    CREATE_SHOP_ABOUT_US,
    UPDATE_SHOP_ABOUT_US,
    DELETE_SHOP_ABOUT_US,
} from '@/services/ApiRoutes';

export interface ValueItem {
    _id?: string;
    id?: string;
    title: string;
    description: string;
    icon?: string;
}

export interface FeatureItem {
    _id?: string;
    id?: string;
    title: string;
    description: string;
    icon?: string;
}

export interface ShopAboutData {
    _id?: string;
    id?: string;
    badge: string;
    title: string;
    subtitle: string;
    story: {
        heading: string;
        paragraph1: string;
        paragraph2: string;
        statNumber: string;
        statLabel: string;
    };
    missionVision: {
        missionTitle: string;
        missionText: string;
        visionTitle: string;
        visionText: string;
    };
    values: ValueItem[];
    whyShopWithUs: FeatureItem[];
    lastUpdated?: string;
    createdAt?: string;
    updatedAt?: string;
}

interface ShopAboutState {
    data: ShopAboutData | null;
    loading: boolean;
    error: string | null;
}

const initialState: ShopAboutState = {
    data: null,
    loading: false,
    error: null,
};

export const fetchShopAboutUs = createAsyncThunk(
    'shopAbout/fetch',
    async (_, { rejectWithValue }) => {
        try {
            const response = await get(GET_SHOP_ABOUT_US);
            return response.data?.data || response.data || null;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch Shop About Us');
        }
    }
);

export const updateShopAboutUs = createAsyncThunk(
    'shopAbout/update',
    async (payload: Partial<ShopAboutData>, { rejectWithValue }) => {
        try {
            const response = await put(UPDATE_SHOP_ABOUT_US, payload);
            return response.data?.data || response.data || response;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Failed to save Shop About Us');
        }
    }
);

export const resetShopAboutUs = createAsyncThunk(
    'shopAbout/reset',
    async (_, { rejectWithValue }) => {
        try {
            const response = await del(DELETE_SHOP_ABOUT_US);
            return response.data?.data || response.data || response;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Failed to reset Shop About Us');
        }
    }
);

const shopAboutSlice = createSlice({
    name: 'shopAbout',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchShopAboutUs.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchShopAboutUs.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(fetchShopAboutUs.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(updateShopAboutUs.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateShopAboutUs.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(updateShopAboutUs.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(resetShopAboutUs.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(resetShopAboutUs.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(resetShopAboutUs.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearError } = shopAboutSlice.actions;
export default shopAboutSlice.reducer;
